import React from 'react';
import Preview from '../Preview/Preview';
import Login from '../Auth/Login';
import AppIcon from '../Identity/AppIcon';
import {usePageIdentity} from '../Identity/BrowserIdentity';
import { executePublished, getPublished, type PublishedRuntime } from '../../api/publishedAppsApi';
import { useAuth } from '../../context/AuthContext';
import type { PreviewActionExecutor, ComponentRuntimeState } from '../Preview/usePreviewRuntime';

export default function PublishedApp({ slug, pageSlug }: { slug: string; pageSlug?: string }): React.ReactElement {
  const { user, loading } = useAuth();
  const [loaded, setLoaded] = React.useState<{key:string;app:PublishedRuntime} | null>(null);
  const requestKey=slug+'|'+(user?.id||'anonymous');
  const app=loaded?.key===requestKey&&!loading?loaded.app:null;
  const [error, setError] = React.useState('');
  const [needsAuth, setNeedsAuth] = React.useState(false);
  usePageIdentity(app?.displayName || (needsAuth&&!user?'Sign in':error?'Application unavailable':undefined),app?.iconPath);
  React.useEffect(() => {
    let active=true;
    setError('');setNeedsAuth(false);setLoaded(null);
    if (loading) return;
    getPublished(slug).then(value => { if(active){setLoaded({key:requestKey,app:value});setNeedsAuth(false);} }).catch(e => {
      if(!active)return;
      const message=e instanceof Error?e.message:String(e);
      setNeedsAuth(message.includes('Authentication'));setError(message);
    });
    return()=>{active=false;};
  }, [slug, requestKey, loading]);
  if (loading) return <p style={{ padding: 32 }}>Loading…</p>;
  if (needsAuth && !user) return <Login message="Sign in to open this application." />;
  if (error && !app) return <main style={{ padding: 32 }}><h1>Application unavailable</h1><p>{error}</p></main>;
  if (!app) return <p style={{ padding: 32 }}>Loading application…</p>;
  if (pageSlug && !app.document.project.pages.some(page => (page.slug || page.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || page.id) === pageSlug)) {
    return <main style={{ padding: 32 }}><h1>Page not found</h1><p>This application does not contain the requested page.</p><a href={`/apps/${slug}`}>Open its default page</a></main>;
  }

  const executor: PreviewActionExecutor = async (action, _project, componentState, variableState, pageId) => {
    const metadata = action as typeof action & { runtimeInputComponents?: string[]; runtimeInputVariables?: string[] };
    const wantedComponents = new Set(metadata.runtimeInputComponents ?? []);
    const wantedVariables = new Set(metadata.runtimeInputVariables ?? []);
    const componentValues: Record<string, unknown> = {};
    for (const page of app.document.project.pages.filter(candidate => candidate.id === pageId)) for (const component of page.components) if (wantedComponents.has(component.name)) {
      const state = componentState[component.id] as ComponentRuntimeState | undefined;
      componentValues[component.name] = state?.textValue ?? state?.value ?? component.properties.defaultValue ?? '';
    }
    const variables = Object.fromEntries(Object.entries(variableState).filter(([key]) => wantedVariables.has(key)));
    return executePublished(slug, action.id, componentValues, variables, pageId);
  };

  return <main><div style={{ padding: '12px 24px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #d0d7de' }}><span style={{display:'flex',alignItems:'center',gap:10}}><AppIcon iconPath={app.iconPath} size={28}/><strong>{app.displayName}</strong></span>{user && <span>Signed in as {user.displayName}</span>}</div><Preview document={app.document} projectName={app.displayName} executor={executor} published initialPageSlug={pageSlug} /></main>;
}
